import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Chip, SvgIconProps, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Stack } from "@mui/system";
import { ReactNode } from "react";
import colors from "theme/base/colors";

type KeywordSearchMode = "fullText" | "exact";

interface KeywordSearchTip {
  title: string;
  prompt: string;
  chipTooltip: ReactNode;
}

interface Props extends SvgIconProps {
  mode?: KeywordSearchMode;
  onExampleSelect: (example: string) => void;
}

const codeStyle = {
  padding: "2px 4px",
  borderRadius: "4px",
  backgroundColor: "rgba(255,255,255,0.15)",
  fontFamily: "monospace",
  fontSize: "0.9em",
};

const textSx = { color: "white !important" };
const tooltipBodySx = {
  ...textSx,
  fontStyle: "italic",
  opacity: 0.9,
  lineHeight: 1.4,
  maxWidth: "280px",
  "&, & *": {
    color: "white !important",
  },
};

const chipSx = {
  cursor: "pointer",
  backgroundColor: colors.white.alpha28,
  color: colors.white.main,
  border: "none",
  "& .MuiChip-label": {
    color: colors.white.main,
  },
};

function TooltipDescription({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" component="div" sx={tooltipBodySx}>
      {children}
    </Typography>
  );
}

function Code({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={codeStyle}>
      {children}
    </Box>
  );
}

const FULL_TEXT_TIPS: KeywordSearchTip[] = [
  {
    title: "Need every word",
    prompt: "summer vacation",
    chipTooltip: (
      <TooltipDescription>
        <Code>summer vacation</Code> finds notes that mention{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          both
        </Box>{" "}
        words, anywhere, in any order.
      </TooltipDescription>
    ),
  },
  {
    title: "Related forms count",
    prompt: "moving truck",
    chipTooltip: (
      <TooltipDescription>
        <Code>moving truck</Code> also finds <Code>moving trucks</Code> —
        related forms count. Turn on Exact string search if you only want the
        characters you typed.
      </TooltipDescription>
    ),
  },
  {
    title: "Need any word",
    prompt: "John or Smith",
    chipTooltip: (
      <TooltipDescription>
        Put <Code>or</Code> between them, like <Code>John or Smith</Code>.
      </TooltipDescription>
    ),
  },
  {
    title: "Skip a word",
    prompt: "healthy -salad",
    chipTooltip: (
      <TooltipDescription>
        Add a minus before it: <Code>healthy -salad</Code>. You can skip a
        phrase too: <Code>{`healthy -"ice cream"`}</Code>.
      </TooltipDescription>
    ),
  },
  {
    title: "Keep words together",
    prompt: `"golden retriever"`,
    chipTooltip: (
      <TooltipDescription>
        Quotes need the words in that order, right next to each other:{" "}
        <Code>{`"golden retriever"`}</Code>. Related forms still match. For the
        exact characters you type, use Exact string search below.
      </TooltipDescription>
    ),
  },
];

const EXACT_TIPS: KeywordSearchTip[] = [
  {
    title: "Literal text only",
    prompt: "moving truck",
    chipTooltip: (
      <TooltipDescription>
        <Code>moving truck</Code> matches that exact text — not{" "}
        <Code>moving trucks</Code>. Related forms do not count. Turn Exact
        string search off if you want singular to cover plural too.
      </TooltipDescription>
    ),
  },
  {
    title: "Need every term",
    prompt: "dog and cat",
    chipTooltip: (
      <TooltipDescription>
        <Code>dog cat</Code> or <Code>dog and cat</Code> finds notes that
        contain{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          both
        </Box>{" "}
        as literal text.
      </TooltipDescription>
    ),
  },
  {
    title: "Need any term",
    prompt: `dog or "man's best friend"`,
    chipTooltip: (
      <TooltipDescription>
        Put <Code>or</Code> between them, like{" "}
        <Code>{`dog or "man's best friend"`}</Code>.
      </TooltipDescription>
    ),
  },
  {
    title: "Skip a term",
    prompt: "dog and -cat",
    chipTooltip: (
      <TooltipDescription>
        Add a minus before a term to exclude it — always with something to find,
        e.g. <Code>dog and -cat</Code>. Skip a phrase with{" "}
        <Code>{`-"ice cream"`}</Code>.
      </TooltipDescription>
    ),
  },
  {
    title: "Exact phrase",
    prompt: `"golden retriever"`,
    chipTooltip: (
      <TooltipDescription>
        Quotes keep spaces and punctuation as typed:{" "}
        <Code>{`"golden retriever"`}</Code>.
      </TooltipDescription>
    ),
  },
  {
    title: "Group with parentheses",
    prompt: `(dog or "man's best friend") and -"cat"`,
    chipTooltip: (
      <TooltipDescription>
        Combine ideas, e.g.{" "}
        <Code>{`(dog or "man's best friend") and -"cat"`}</Code>.
      </TooltipDescription>
    ),
  },
];

export default function KeywordFilter_Tooltip({
  mode = "fullText",
  onExampleSelect,
  ...props
}: Props) {
  const isExact = mode === "exact";
  const searchTips = isExact ? EXACT_TIPS : FULL_TEXT_TIPS;

  return (
    <Tooltip
      title={
        <Stack sx={{ p: "1rem", gap: 1.5 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              ...textSx,
              display: "flex",
              alignItems: "center",
            }}
          >
            <HelpOutlineIcon
              sx={{ verticalAlign: "middle", mr: 1 }}
              fontSize="inherit"
            />
            {isExact ? "Exact String Search Tips" : "Search Tips"}
          </Typography>
          <Typography variant="body2" component="div" sx={textSx}>
            {isExact ? (
              <>
                Matches the exact characters you type in notes and transcripts —
                not close spellings or related forms. Combine terms with{" "}
                <Code>and</Code>, <Code>or</Code>, <Code>-exclude</Code>,
                quotes, and parentheses. Turn the checkbox off to search related
                word forms again. Click a tip to try it:
              </>
            ) : (
              <>
                Words can show up anywhere, and related forms count —{" "}
                <Code>jog</Code> finds jogging. Turn on Exact string search for
                literal text and optional <Code>and</Code>/<Code>or</Code>/
                <Code>-</Code> logic. Click a tip to try it:
              </>
            )}
          </Typography>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {searchTips.map((tip) => (
              <Tooltip
                key={tip.title}
                title={tip.chipTooltip}
                placement="bottom"
                enterTouchDelay={0}
                arrow
              >
                <span>
                  <Chip
                    label={tip.title}
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onExampleSelect(tip.prompt);
                    }}
                    sx={chipSx}
                  />
                </span>
              </Tooltip>
            ))}
          </Stack>
        </Stack>
      }
      arrow
    >
      <IconButton>
        <HelpOutlineIcon {...props} />
      </IconButton>
    </Tooltip>
  );
}
